import { FormGroup } from '@angular/forms';
import { format, formatISO } from 'date-fns';

import { FieldType } from '../../enums/field-type.enum';
import { splitFilterString } from '../../filter.utils';
import { SearchField } from '../../models/search/search-field.model';
import { Option } from '../../../shared/models/option.model';
import { getTopLevelGroupings, GroupingCharacterType, quoteString } from '../../string.utils';

export interface Rule {
  field: SearchField
  operator: string
  text: string
  chips: string[]
  'number': number
  'boolean': boolean
  date: {
    timestampDate: string
    timestampTime: string
  }
  regex: boolean
}

export const emptyRule = {
  field: { name: '', type: 'string', facet: false },
  date: { timestampDate: null, timestampTime: null },
} as Rule;

const operatorOptions: { [key: string]: Option[] } = {
  'string': [
    { value: '=', label: 'is' },
    { value: '!=', label: 'is not' },
  ],
  'number': [
    { value: '=' },
    { value: '!=' },
    { value: '<' },
    { value: '<=' },
    { value: '>=' },
    { value: '>' },
  ],
  'boolean': [
    { value: '=', label: 'is' },
  ],
  date: [
    { value: '<' },
    { value: '<=' },
    { value: '>=' },
    { value: '>' },
    { value: '=' },
    { value: '!=' },
  ],
};

export function getOperatorOptionsByType(type: FieldType): Option[] {
  return operatorOptions[simplifyFieldType(type)];
}

export function simplifyFieldType(type: FieldType): string {
  switch (type) {
    case FieldType.Boolean:
      return 'boolean';
    case FieldType.Byte:
    case FieldType.Double:
    case FieldType.Float:
    case FieldType.HalfFloat:
    case FieldType.Integer:
    case FieldType.Long:
    case FieldType.ScaledFloat:
    case FieldType.Short:
      return 'number';
    case FieldType.Date:
      return 'date';
    default:
      return 'string';
  }
}

export function buildRuleField(cache: Map<string, SearchField>, field: string): Partial<SearchField> {
  field = field || '';
  const cachedField = cache.get(field);
  const fieldName = quoteString(field);
  return cachedField ?
    { ...cachedField, name: fieldName } :
    { type: FieldType.String, name: fieldName };
}

export function getDefaultFieldType(fieldKey: string): { type: FieldType } {
  return { type: /created|timestamp|modified/.test(fieldKey) ? FieldType.Date : FieldType.String };
}

/**
 * For rules with same field names and operators combine into one rule with multiple chips (only for field types equal to 'strings')
 * @param {Rule[]} rules - list of rules which may contain common field names and operators
 * @return {Rule[]} - list of rules of which common field names and operators are consolidated to one rule
 */
export function consolidateCommonRules(rules: Rule[]): Rule[] {
  const consolidatedRules = {};
  for (const [i, rule] of Object.entries(rules)) {
    const key = rule.field.name + rule.operator + rule.regex;
    if (!consolidatedRules[key]) {
      consolidatedRules[key] = rule;
    } else if (rule.field.type === 'string') {
      consolidatedRules[key].chips.push(rule.chips[0]);
    } else {
      // allow multiple rules with same key/operator pairs
      consolidatedRules[key + i] = rule;
    }
  }
  return Object.values(consolidatedRules);
}

export function convertStringToRule(cache: Map<string, SearchField>, input?: string): Rule | null {
  if (!input) {
    return emptyRule;
  }
  const parts = splitFilterString(input);
  const field = buildRuleField(cache, parts[0]) as SearchField;
  const fieldType = simplifyFieldType(field.type);
  const isRegex = parts[1].indexOf('~') > -1;
  let value;
  switch (fieldType) {
    case 'boolean':
      value = Boolean(parts[2]);
      break;
    case 'date': {
      const d = new Date(parts[2]);
      value = d.toString() === 'Invalid Date' ? new Date() : d;
      break;
    }
    case 'number': {
      const n = Number(parts[2]);
      value = Number.isNaN(n) ? null : n;
      break;
    }
    case 'string':
      value = parts[2];
      break;
  }
  if (value === null) {
    return null;
  }
  return {
    field: field,
    operator: parts[1].replace('!~', '!=').replace('~', ''),
    chips: fieldType === 'string'
      ? isRegex ? getTopLevelGroupings(value, GroupingCharacterType.Parentheses) : [value]
      : [],
    'number': fieldType === 'number' ? value : null,
    'boolean': fieldType === 'boolean' ? value : null,
    date: fieldType === 'date'
      ? { timestampDate: format(value, 'yyyy-MM-dd HH:mm'), timestampTime: format(value, 'HH:mm') }
      : { timestampDate: null, timestampTime: null },
    text: '',
    regex: isRegex,
  };
}

export function convertRuleToStringArray(cache: Map<string, SearchField>, rule: Rule): string[] {
  const key = rule.field?.name || rule.field as unknown as string;
  const field = cache.get(key) || getDefaultFieldType(key);
  switch (simplifyFieldType(field.type)) {
    case 'number':
      return typeof rule.number === 'number' ? [`${key}${rule.operator}${rule.number}`] : [];
    case 'boolean':
      return [`${key}${rule.operator}${rule.boolean}`];
    case 'date': {
      const sDate = `${format(new Date(rule.date.timestampDate), 'yyyy-MM-dd')} ${rule.date.timestampTime}`;
      const timestamp = formatISO(new Date(sDate));
      return [`${key}${rule.operator}${timestamp}`];
    }
    default: {
      const chips = rule.regex ? [rule.chips.map((v) => `(${v})`).join('|')] : rule.chips || [];
      return chips.map((chip) => {
        const operator = `${rule.operator}${rule.regex ? '~' : ''}`.replace('!=~', '!~');
        return `${key}${operator}${chip}`;
      });
    }
  }
}

export function getOperatorOptions(cache: Map<string, SearchField>, rule: FormGroup): Option[] {
  const field = cache.get(rule.controls.field.value) || getDefaultFieldType(rule.controls.field.value);
  const options = getOperatorOptionsByType(field ? field.type : FieldType.String);
  return options;
}

export function getValueInterface(cache: Map<string, SearchField>, rule: FormGroup): string {
  const field = cache.get(rule.controls.field.value) || getDefaultFieldType(rule.controls.field.value);
  const operator = rule.controls.operator.value || '';
  const fieldType = simplifyFieldType(field.type);
  if (fieldType === 'number') {
    return 'number';
  }
  if (operator === 'exists' || fieldType === 'boolean') {
    return 'boolean';
  }
  if (fieldType === 'date') {
    return 'date';
  }
  if (operator.includes('contains')) {
    return 'input';
  }
  return 'chips';
}
